import React, { useState } from 'react';
import {
  Wand2, ZoomIn, ZoomOut, Maximize2, MoveHorizontal, Download, FileText, FileCode2, ImageIcon, Printer,
  Columns2, AlignLeft, RectangleHorizontal, RectangleVertical, Loader2, SlidersHorizontal, ChevronDown, PenLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const Seg = ({ value, onChange, options, testId, disabled }) => (
  <div className={`inline-flex rounded-lg bg-slate-100 p-0.5 ${disabled ? 'opacity-50' : ''}`} data-testid={testId}>
    {options.map((o) => (
      <button key={o.value} type="button" disabled={disabled} title={o.title} onClick={() => onChange(o.value)}
        data-testid={`${testId}-${o.value}`} aria-pressed={value === o.value}
        className={`h-8 px-2.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 transition-colors duration-150 disabled:cursor-not-allowed ${value === o.value ? 'bg-white text-[#00247D] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>
        {o.icon}{o.label}
      </button>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</span>
    {children}
  </div>
);

const ZoomControls = ({ zoom }) => {
  const btn = 'h-9 w-9 inline-flex items-center justify-center text-slate-600 hover:text-[#00247D] hover:bg-slate-50 transition-colors';
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="bracket-zoom">
      <button type="button" className={btn} onClick={() => zoom.zoomBy(1 / 1.2)} data-testid="bracket-zoom-out" aria-label="Zoom out"><ZoomOut className="w-4 h-4" /></button>
      <span className="w-12 text-center text-xs font-bold text-slate-700 tabular-nums" data-testid="bracket-zoom-level">{zoom.pct}%</span>
      <button type="button" className={btn} onClick={() => zoom.zoomBy(1.2)} data-testid="bracket-zoom-in" aria-label="Zoom in"><ZoomIn className="w-4 h-4" /></button>
      <button type="button" className={`${btn} border-l border-slate-200 ${zoom.mode === 'width' ? 'text-[#00247D] bg-slate-50' : ''}`} onClick={() => zoom.setMode('width')} data-testid="bracket-fit-width" title="Fit width"><MoveHorizontal className="w-4 h-4" /></button>
      <button type="button" className={`${btn} ${zoom.mode === 'page' ? 'text-[#00247D] bg-slate-50' : ''}`} onClick={() => zoom.setMode('page')} data-testid="bracket-fit-page" title="Fit to page"><Maximize2 className="w-4 h-4" /></button>
    </div>
  );
};

const EXPORTS = [
  ['pdf', 'PDF · vector', FileText], ['svg', 'SVG · vector', FileCode2], ['png', 'PNG · 300 dpi', ImageIcon], ['print', 'Print', Printer],
];

const ExportMenu = ({ onExport, busy }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" className="h-9" data-testid="bracket-export-btn" disabled={!!busy}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Export
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Download bracket</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {EXPORTS.map(([k, label, Icon]) => (
        <DropdownMenuItem key={k} onClick={() => onExport(k)} data-testid={`bracket-export-${k}`}>
          <Icon className="w-4 h-4 mr-2 text-slate-500" />{label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const BracketToolbar = ({ settings, update, layout, model, zoom, onOptimize, onExport, busy, allowBlank, blank, setBlank, onCustomize }) => {
  const canMirror = model.kind === 'single' && model.canMirror;
  const [open, setOpen] = useState(false);
  const icon = 'w-3.5 h-3.5';
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3 flex flex-wrap items-end gap-x-5 gap-y-3" data-testid="bracket-toolbar">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="bracket-options-toggle"
        className="md:hidden h-9 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 inline-flex items-center gap-2">
        <SlidersHorizontal className="w-4 h-4" />Layout options
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={open ? 'contents' : 'hidden md:contents'} data-testid="bracket-options">
      <Field label="Layout">
        <Seg testId="bracket-layout" value={layout.mode === 'mirrored' ? 'mirrored' : 'classic'} disabled={!canMirror} onChange={(v) => update({ layout: v })}
          options={[{ value: 'mirrored', label: 'Mirrored', icon: <Columns2 className={icon} /> }, { value: 'classic', label: 'Classic', icon: <AlignLeft className={icon} /> }]} />
      </Field>
      <Field label="Paper">
        <Seg testId="bracket-paper" value={settings.paper} onChange={(v) => update({ paper: v })}
          options={[{ value: 'letter', label: 'Letter', title: '8.5 × 11 in' }, { value: 'tabloid', label: 'Tabloid', title: '11 × 17 in' }]} />
      </Field>
      <Field label="Orientation">
        <Seg testId="bracket-orientation" value={settings.orientation} onChange={(v) => update({ orientation: v })}
          options={[{ value: 'landscape', label: 'Landscape', icon: <RectangleHorizontal className={icon} /> }, { value: 'portrait', label: 'Portrait', icon: <RectangleVertical className={icon} /> }]} />
      </Field>
      <Field label="Pages">
        <Seg testId="bracket-pages" value={String(settings.pages)} disabled={layout.mode === 'consolation'} onChange={(v) => update({ pages: Number(v) })}
          options={[{ value: '1', label: '1 page' }, { value: '2', label: '2 pages' }]} />
      </Field>
      <Field label="Match info">
        <Seg testId="bracket-details" value={settings.details} onChange={(v) => update({ details: v })}
          options={[{ value: 'auto', label: 'Auto' }, { value: 'on', label: 'Show' }, { value: 'off', label: 'Hide' }]} />
      </Field>
      {allowBlank && (
        <Field label="Team names">
          <div className="flex flex-wrap items-center gap-2">
            <Seg testId="bracket-names" value={blank ? 'blank' : 'filled'} onChange={(v) => setBlank(v === 'blank')}
              options={[{ value: 'filled', label: 'Placeholders' }, { value: 'blank', label: 'Blank write-in' }]} />
            {blank && onCustomize && (
              <Button onClick={onCustomize} className="h-9 bg-[#CF142B] hover:bg-[#A81022] animate-in fade-in zoom-in-95 duration-200" data-testid="bracket-customize-btn">
                <PenLine className="w-4 h-4 mr-2" />Customize Bracket
              </Button>
            )}
          </div>
        </Field>
      )}
      </div>
      <div className="flex flex-wrap items-end gap-2 lg:ml-auto">
        {allowBlank && onCustomize && (
          <Button onClick={onCustomize} className="md:hidden h-9 bg-[#CF142B] hover:bg-[#A81022]" data-testid="bracket-customize-mobile-btn">
            <PenLine className="w-4 h-4 mr-2" />Customize
          </Button>
        )}
        <Button onClick={onOptimize} className="h-9 bg-[#00247D] hover:bg-[#001A5C]" data-testid="bracket-auto-optimize-btn">
          <Wand2 className="w-4 h-4 mr-2" />Auto Optimize
        </Button>
        <ZoomControls zoom={zoom} />
        <ExportMenu onExport={onExport} busy={busy} />
      </div>
    </div>
  );
};
